import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import bioscopeImg from '@/assets/InternalIcons/bioscope-3-0-2.png';

import { formatWinchMeters } from '../../helpers/formatWinchValue';
import {
    extentsFromLimits,
    formatTick,
    makeTicks,
    metersToPx,
    plotLayoutFromExtents,
    plotRect,
} from '../../helpers/penPlotGeometry';

import styles from './PenPlot.module.scss';

interface PenPlotProps {
    distance: number | null;
    depth: number | null;
    targetDistance: number | null;
    targetDepth: number | null;
    previewDistance: number | null;
    previewDepth: number | null;
    moving: boolean;
    distanceMax: number;
    depthMax: number;
}

const BIOSCOPE_PX = 56;
const GHOST_PX = 40;

function BioscopeMarker({ ghost = false }: { ghost?: boolean }) {
    const size = ghost ? GHOST_PX : BIOSCOPE_PX;
    return (
        <image
            data-plot={ghost ? 'bioscope-ghost' : 'bioscope'}
            href={bioscopeImg}
            x={-size / 2}
            y={-size / 2}
            width={size}
            height={size}
            preserveAspectRatio="xMidYMid meet"
            opacity={ghost ? 0.55 : 1}
        />
    );
}

export function PenPlot(props: PenPlotProps) {
    const {
        distance,
        depth,
        targetDistance,
        targetDepth,
        previewDistance,
        previewDepth,
        moving,
        distanceMax,
        depthMax,
    } = props;
    const { t, i18n } = useTranslation();
    const uid = useId().replace(/:/g, '');
    const waterId = `winch-water-${uid}`;
    const sheenId = `winch-sheen-${uid}`;
    const clipId = `winch-clip-${uid}`;
    const extents = extentsFromLimits(distanceMax, depthMax);
    const layout = plotLayoutFromExtents(extents);
    const rect = plotRect(layout);
    const xTicks = makeTicks(extents.xMin, extents.xMax, 6);
    const yTicks = makeTicks(extents.yMin, extents.yMax, 6);
    const toPx = (xM: number, yM: number) => metersToPx(xM, yM, extents, layout);
    const here = distance != null && depth != null ? toPx(distance, depth) : null;
    const dest =
        moving && targetDistance != null && targetDepth != null
            ? toPx(targetDistance, targetDepth)
            : null;
    const previewDiffers =
        previewDistance != null &&
        previewDepth != null &&
        (distance == null ||
            depth == null ||
            Math.abs(previewDistance - distance) > 0.05 ||
            Math.abs(previewDepth - depth) > 0.05);
    const preview =
        !moving && previewDiffers && previewDistance != null && previewDepth != null
            ? toPx(previewDistance, previewDepth)
            : null;

    return (
        <svg
            className={styles.plot}
            viewBox={`0 0 ${layout.width} ${layout.height}`}
            preserveAspectRatio="xMidYMid meet"
            role="img"
            aria-label={t('winchControl.graphic.ariaLabel')}>
            <defs>
                {/* Stop colours come from the stylesheet, so the water follows the theme. */}
                <linearGradient id={waterId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" className={styles.water0} />
                    <stop offset="18%" className={styles.water1} />
                    <stop offset="62%" className={styles.water2} />
                    <stop offset="100%" className={styles.water3} />
                </linearGradient>
                <linearGradient id={sheenId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" className={styles.sheen} stopOpacity="0.55" />
                    <stop offset="18%" className={styles.sheen} stopOpacity="0.08" />
                    <stop offset="100%" className={styles.sheen} stopOpacity="0" />
                </linearGradient>
                <clipPath id={clipId}>
                    <rect x={rect.x} y={rect.y} width={rect.w} height={rect.h} rx="4" />
                </clipPath>
            </defs>
            <rect
                x={rect.x}
                y={rect.y}
                width={rect.w}
                height={rect.h}
                rx="4"
                fill={`url(#${waterId})`}
                className={styles.well}
            />
            <g clipPath={`url(#${clipId})`}>
                {xTicks.map((tick) => {
                    const { x } = toPx(tick, extents.yMin);
                    return (
                        <line
                            key={`xg-${tick}`}
                            x1={x}
                            y1={rect.y}
                            x2={x}
                            y2={rect.y + rect.h}
                            className={styles.gridX}
                        />
                    );
                })}
                {yTicks.map((tick) => {
                    const { y } = toPx(extents.xMin, tick);
                    return (
                        <line
                            key={`yg-${tick}`}
                            x1={rect.x}
                            y1={y}
                            x2={rect.x + rect.w}
                            y2={y}
                            className={styles.gridY}
                        />
                    );
                })}
                <rect
                    x={rect.x}
                    y={rect.y}
                    width={rect.w}
                    height={rect.h}
                    fill={`url(#${sheenId})`}
                />
                <line
                    x1={rect.x}
                    y1={rect.y + 2}
                    x2={rect.x + rect.w}
                    y2={rect.y + 2}
                    className={styles.surface}
                />
                <line
                    x1={rect.x + rect.w - 1}
                    y1={rect.y}
                    x2={rect.x + rect.w - 1}
                    y2={rect.y + rect.h}
                    className={styles.penEdge}
                />
                {here && (
                    <>
                        <line
                            x1={rect.x}
                            y1={here.y}
                            x2={here.x}
                            y2={here.y}
                            className={styles.hair}
                        />
                        <line
                            x1={here.x}
                            y1={rect.y}
                            x2={here.x}
                            y2={here.y}
                            className={styles.hair}
                        />
                    </>
                )}
                {here && dest && (
                    <line
                        x1={here.x}
                        y1={here.y}
                        x2={dest.x}
                        y2={dest.y}
                        className={styles.targetLine}
                    />
                )}
                {here && preview && (
                    <line
                        x1={here.x}
                        y1={here.y}
                        x2={preview.x}
                        y2={preview.y}
                        className={styles.previewLine}
                    />
                )}
                {dest && (
                    <g transform={`translate(${dest.x} ${dest.y})`}>
                        <BioscopeMarker ghost />
                    </g>
                )}
                {preview && (
                    <g transform={`translate(${preview.x} ${preview.y})`}>
                        <BioscopeMarker ghost />
                    </g>
                )}
                {here && (
                    <g transform={`translate(${here.x} ${here.y})`}>
                        <circle r={BIOSCOPE_PX / 2 + 2} className={styles.markerHalo} />
                        <BioscopeMarker />
                    </g>
                )}
            </g>
            {xTicks.map((tick) => {
                const { x } = toPx(tick, extents.yMax);
                return (
                    <g key={`xt-${tick}`}>
                        <line
                            x1={x}
                            y1={rect.y + rect.h}
                            x2={x}
                            y2={rect.y + rect.h + 6}
                            className={styles.tickMark}
                        />
                        <text x={x} y={rect.y + rect.h + 20} className={styles.tick}>
                            {formatTick(tick, i18n.language)}
                        </text>
                    </g>
                );
            })}
            {yTicks.map((tick) => {
                const { y } = toPx(extents.xMin, tick);
                return (
                    <g key={`yt-${tick}`}>
                        <line
                            x1={rect.x - 6}
                            y1={y}
                            x2={rect.x}
                            y2={y}
                            className={styles.tickMark}
                        />
                        <text x={rect.x - 10} y={y + 4} textAnchor="end" className={styles.tick}>
                            {formatTick(tick, i18n.language)}
                        </text>
                    </g>
                );
            })}
            <text x={rect.x + rect.w / 2} y={layout.height - 6} className={styles.axisLabel}>
                {t('winchControl.card.distance')} ({t('winchControl.position.meters')})
            </text>
            <text
                transform={`translate(16 ${rect.y + rect.h / 2}) rotate(-90)`}
                className={styles.axisLabel}>
                {t('winchControl.card.depth')} ({t('winchControl.position.meters')})
            </text>
            {here && (
                <>
                    <text
                        data-plot="hair-distance"
                        x={(rect.x + here.x) / 2}
                        y={here.y - 8}
                        className={styles.hairLabel}
                        textAnchor="middle">
                        {t('winchControl.card.distance')} {formatWinchMeters(distance)}
                    </text>
                    <text
                        data-plot="hair-depth"
                        x={here.x + 10}
                        y={(rect.y + here.y) / 2}
                        className={styles.hairLabel}>
                        {t('winchControl.card.depth')} {formatWinchMeters(depth)}
                    </text>
                </>
            )}
        </svg>
    );
}
